import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from its previous value to the new value.
 * Uses ease-out timing (starts fast, finishes slow).
 * Returns the current displayed value and the direction of change.
 */
export function useAnimatedCount(target: number, duration = 600) {
  const [display, setDisplay] = useState(target);
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  const prevTarget = useRef(target);
  const rafRef = useRef<number>();
  const directionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const from = prevTarget.current;
    const to = target;
    prevTarget.current = target;

    if (from === to) return;

    const diff = to - from;
    setDirection(diff > 0 ? 'up' : 'down');

    // Clear any existing direction reset timeout
    if (directionTimeoutRef.current) {
      clearTimeout(directionTimeoutRef.current);
    }

    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out quintic: starts fast, finishes very slow
      const eased = 1 - Math.pow(1 - progress, 5);
      const current = Math.round(from + diff * eased);

      setDisplay(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(to);
        // Keep the highlight for a moment after animation finishes
        directionTimeoutRef.current = setTimeout(() => setDirection(null), 400);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (directionTimeoutRef.current) clearTimeout(directionTimeoutRef.current);
    };
  }, []);

  return { display, direction };
}
